// The calendar's shared pieces (PMU-265): kinds, the month grid, the list of what's coming and a
// read-only timetable grid. Used by the console, the public page (and its embed) and the parent
// app, so a school's calendar looks the same wherever it is opened.
import { useMemo, useState, type ReactNode } from "react";
import { Button, Icon } from "@vavr/ui";
import "./calendar.css";

export type Kind = "event" | "holiday" | "no_school" | "early_release" | "late_start" | "term" | "exam" | "meeting" | "sports" | "trip";
export type Audience = "public" | "families" | "staff";
export interface CalEvent { id: string; title: string; description: string | null; kind: Kind; start_date: string; end_date: string; start_time: string | null; end_time: string | null; location: string | null; minutes: number | null; audience?: Audience; bell_schedule_id?: string | null }
export interface Period { code: string; name: string; kind: string; start_time: string; end_time: string; seq?: number }
export interface Slot { weekday: number; period_code: string; subject: string; teacher_account_id?: string | null; teacher?: string | null; location: string | null }

export const KIND: Record<Kind, { label: string; tone: string }> = {
  event: { label: "Event", tone: "brand" }, holiday: { label: "Holiday", tone: "accent" }, no_school: { label: "No school", tone: "danger" },
  early_release: { label: "Early release", tone: "warning" }, late_start: { label: "Late start", tone: "warning" }, term: { label: "Term", tone: "info" },
  exam: { label: "Exams", tone: "violet" }, meeting: { label: "Meeting", tone: "neutral" }, sports: { label: "Sports", tone: "success" }, trip: { label: "Trip", tone: "info" },
};
export const KINDS = Object.keys(KIND) as Kind[];
export const AUDIENCE: Record<Audience, string> = { public: "Everyone", families: "Families and staff", staff: "Staff only" };
export const WEEKDAYS = [{ d: 1, l: "Mon", long: "Monday" }, { d: 2, l: "Tue", long: "Tuesday" }, { d: 3, l: "Wed", long: "Wednesday" }, { d: 4, l: "Thu", long: "Thursday" }, { d: 5, l: "Fri", long: "Friday" }, { d: 6, l: "Sat", long: "Saturday" }, { d: 0, l: "Sun", long: "Sunday" }];

export const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const fmt = (d: string, o: Intl.DateTimeFormatOptions) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { ...o, timeZone: "UTC" });
export const longDay = (d: string) => fmt(d, { weekday: "long", month: "long", day: "numeric" });
export const shortDay = (d: string) => fmt(d, { weekday: "short", month: "short", day: "numeric" });
export const clock = (t: string | null) => { if (!t) return ""; const [h, m] = t.split(":").map(Number) as [number, number]; return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
export const when = (e: CalEvent) => {
  const days = e.start_date === e.end_date ? shortDay(e.start_date) : `${shortDay(e.start_date)} – ${shortDay(e.end_date)}`;
  const time = e.start_time ? `, ${clock(e.start_time)}${e.end_time ? `–${clock(e.end_time)}` : ""}` : "";
  const moved = e.kind === "early_release" && e.minutes ? ` · ${e.minutes} min early` : e.kind === "late_start" && e.minutes ? ` · ${e.minutes} min late` : "";
  return `${days}${time}${moved}`;
};
const weekdayOf = (d: string) => new Date(`${d}T12:00:00Z`).getUTCDay();

/** A month of the calendar, Monday first, each day with its items; `onDay`/`onEvent` make it clickable. */
export function MonthView({ events, month, setMonth, today, onDay, onEvent, compact }: { events: CalEvent[]; month: string; setMonth: (m: string) => void; today: string; onDay?: (date: string) => void; onEvent?: (e: CalEvent) => void; compact?: boolean }) {
  const first = `${month}-01`;
  const start = addDays(first, -((weekdayOf(first) + 6) % 7));
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const weeks = days.slice(35, 42).every((d) => d.slice(0, 7) !== month) ? 5 : 6;
  const next = (n: number) => { const d = new Date(`${first}T12:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); setMonth(d.toISOString().slice(0, 7)); };
  const on = (d: string) => events.filter((e) => e.start_date <= d && e.end_date >= d);
  return (
    <section className={`pcal-month${compact ? " pcal-month--compact" : ""}`} aria-label={fmt(first, { month: "long", year: "numeric" })}>
      <header className="pcal-month__head">
        <h2>{fmt(first, { month: "long", year: "numeric" })}</h2>
        <span className="pcal-month__nav">
          <Button size="sm" variant="ghost" icon="chevron-left" aria-label="Previous month" onClick={() => next(-1)}>{compact ? "" : "Previous"}</Button>
          <Button size="sm" variant="ghost" onClick={() => setMonth(today.slice(0, 7))}>Today</Button>
          <Button size="sm" variant="ghost" aria-label="Next month" onClick={() => next(1)}>{compact ? "" : "Next"} <Icon name="chevron-right" /></Button>
        </span>
      </header>
      <div className="pcal-month__grid" role="grid" aria-label={fmt(first, { month: "long", year: "numeric" })}>
        <div role="row" className="pcal-month__row">{WEEKDAYS.map((w) => <span key={w.d} className="pcal-month__dow" role="columnheader" aria-label={w.long}>{w.l}</span>)}</div>
        {Array.from({ length: weeks }, (_, wk) => (
        <div role="row" className="pcal-month__row" key={wk}>
        {days.slice(wk * 7, wk * 7 + 7).map((d) => {
          const items = on(d);
          const out = d.slice(0, 7) !== month;
          const closed = items.some((e) => e.kind === "no_school" || e.kind === "holiday");
          return (
            <div key={d} role="gridcell" className={`pcal-day${out ? " pcal-day--out" : ""}${d === today ? " pcal-day--today" : ""}${closed ? " pcal-day--closed" : ""}`}
              onClick={onDay ? () => onDay(d) : undefined} aria-label={`${longDay(d)}${items.length ? `: ${items.map((e) => e.title).join(", ")}` : ""}`}>
              <span className="pcal-day__num">{Number(d.slice(8))}</span>
              {items.slice(0, compact ? 2 : 3).map((e) => (
                <button type="button" key={e.id} className={`pcal-chip pcal-chip--${KIND[e.kind].tone}`} title={`${e.title} · ${when(e)}`}
                  onClick={(ev) => { ev.stopPropagation(); onEvent?.(e); }}>{e.start_time && e.start_date === d ? `${clock(e.start_time).replace(":00", "")} ` : ""}{e.title}</button>
              ))}
              {items.length > (compact ? 2 : 3) && <span className="pcal-day__more">+{items.length - (compact ? 2 : 3)} more</span>}
            </div>
          );
        })}
        </div>
        ))}
      </div>
    </section>
  );
}

/** What's coming, grouped by month: the dates, the item and its kind. */
export function EventList({ events, onEvent, empty = "Nothing on the calendar yet.", limit, level = 2 }: { events: CalEvent[]; onEvent?: (e: CalEvent) => void; empty?: string; limit?: number; level?: 2 | 3 | 4 }) {
  const H = `h${level}` as "h2";
  const shown = limit ? events.slice(0, limit) : events;
  const groups = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    for (const e of shown) (m.get(e.start_date.slice(0, 7)) ?? m.set(e.start_date.slice(0, 7), []).get(e.start_date.slice(0, 7))!).push(e);
    return [...m.entries()];
  }, [shown]);
  if (!shown.length) return <p className="pcal-empty">{empty}</p>;
  return (
    <div className="pcal-list">
      {groups.map(([m, list]) => (
        <section key={m} aria-label={fmt(`${m}-01`, { month: "long", year: "numeric" })}>
          <H className="pcal-list__month">{fmt(`${m}-01`, { month: "long", year: "numeric" })}</H>
          <ul>
            {list.map((e) => {
              const Tag = onEvent ? "button" : "div";
              return (
                <li key={e.id}>
                  <Tag className="pcal-item" {...(onEvent ? { type: "button" as const, onClick: () => onEvent(e) } : {})}>
                    <span className="pcal-item__date" aria-hidden="true"><b>{Number(e.start_date.slice(8))}</b><span>{fmt(e.start_date, { weekday: "short" })}</span></span>
                    <span className="pcal-item__main">
                      <b>{e.title}</b>
                      <span>{when(e)}{e.location ? ` · ${e.location}` : ""}</span>
                      {e.description && <span className="pcal-item__desc">{e.description}</span>}
                    </span>
                    <span className={`pcal-tag pcal-tag--${KIND[e.kind].tone}`}>{KIND[e.kind].label}</span>
                    {e.audience && e.audience !== "public" && <span className="pcal-tag pcal-tag--neutral">{AUDIENCE[e.audience]}</span>}
                  </Tag>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** A class's week: periods down, weekdays across; `cell` renders an editable cell instead. */
export function TimetableGrid({ periods, slots, days = [1, 2, 3, 4, 5], cell, today }: { periods: Period[]; slots: Slot[]; days?: number[]; cell?: (weekday: number, p: Period, s: Slot | undefined) => ReactNode; today?: number }) {
  const cols = WEEKDAYS.filter((w) => days.includes(w.d));
  return (
    <div className="pcal-tt" role="table" style={{ ["--pcal-days" as string]: cols.length }}>
      <div className="pcal-tt__row pcal-tt__head" role="row">
        <span role="columnheader">Period</span>
        {cols.map((w) => <span role="columnheader" key={w.d} className={w.d === today ? "pcal-tt__today" : undefined}>{w.long}</span>)}
      </div>
      {periods.map((p) => (
        <div className={`pcal-tt__row${p.kind !== "class" ? " pcal-tt__row--break" : ""}`} role="row" key={p.code}>
          <span role="rowheader" className="pcal-tt__period"><b>{p.name}</b><span>{clock(p.start_time)}–{clock(p.end_time)}</span></span>
          {cols.map((w) => {
            const s = slots.find((x) => x.weekday === w.d && x.period_code === p.code);
            if (cell) return <span role="cell" key={w.d} className="pcal-tt__cellwrap">{cell(w.d, p, s)}</span>;
            return (
              <span role="cell" key={w.d} className={`pcal-tt__cell${s ? "" : " pcal-tt__cell--empty"}${w.d === today ? " pcal-tt__cell--today" : ""}`}>
                {s ? <><b>{s.subject}</b>{(s.teacher || s.location) && <span>{[s.teacher, s.location].filter(Boolean).join(" · ")}</span>}</> : p.kind !== "class" ? <span>{p.name}</span> : null}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Month and list, switchable (the console, the public page). */
export function CalendarViews({ events, today, onEvent, onDay, compact }: { events: CalEvent[]; today: string; onEvent?: (e: CalEvent) => void; onDay?: (d: string) => void; compact?: boolean }) {
  const [view, setView] = useState<"month" | "list">(compact ? "list" : "month");
  const [month, setMonth] = useState(today.slice(0, 7));
  const coming = events.filter((e) => e.end_date >= today);
  return (
    <div className="pmu-stack">
      <div className="pcal-switch" role="tablist" aria-label="View">
        <button type="button" role="tab" aria-selected={view === "month"} onClick={() => setView("month")}><Icon name="calendar" /> Month</button>
        <button type="button" role="tab" aria-selected={view === "list"} onClick={() => setView("list")}><Icon name="list" /> List</button>
      </div>
      {view === "month" ? <MonthView events={events} month={month} setMonth={setMonth} today={today} onEvent={onEvent} onDay={onDay} compact={compact} />
        : <EventList events={coming} onEvent={onEvent} empty="Nothing coming up." />}
    </div>
  );
}
